import { useState } from 'react'
import IconButton from '@mui/material/IconButton'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import COLORS from 'lib/colors'
import { GridValidRowModel } from '@mui/x-data-grid-premium'
import { TooltipIfEnabled } from 'components/UI/TooltipIfEnabled/TooltipIfEnabled'

type RowMenuItem<Row extends GridValidRowModel> = {
  key: string
  label: React.ReactNode
  onClick: (row: Row) => void
  disabled?: boolean
  tooltip?: React.ReactNode
}

type DataGridRowMenuProps<Row extends GridValidRowModel> = {
  row: Row
  label: string
  items: Array<RowMenuItem<Row>>
  tooltip?: React.ReactNode
  disabled?: boolean
  menuWidth?: number
  onOpen?: (row: Row) => void
  onClose?: () => void
}

function MenuTriggerButton({
  label,
  onClick,
  tooltip,
  disabled,
}: {
  label: string
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void
  tooltip?: React.ReactNode
  disabled?: boolean
}) {

  return (
    <TooltipIfEnabled enabled={!!tooltip} label={label}>
      <IconButton
        className='actionButton'
        onClick={(event) => {
          event.stopPropagation()
          onClick(event)
        }}
        aria-label={label}
        size='small'
        disabled={disabled}
        sx={{ color: COLORS.charcoal, padding: 0.5 }}
      >
        <MoreHorizIcon />
      </IconButton>
    </TooltipIfEnabled>
  )
}

export default function DataGridRowMenu<Row extends GridValidRowModel>({
  row,
  label,
  items,
  tooltip,
  disabled = false,
  menuWidth = 240,
  onOpen,
  onClose,
}: DataGridRowMenuProps<Row>) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  function handleOpen(event: React.MouseEvent<HTMLButtonElement>) {
    if (disabled) return

    if (anchorEl !== event.currentTarget) {
      setAnchorEl(event.currentTarget)
      onOpen?.(row)
    }
  }

  function handleClose() {
    setAnchorEl(null)
    onClose?.()
  }

  return (
    <>
      <MenuTriggerButton
        label={label}
        onClick={handleOpen}
        tooltip={tooltip}
        disabled={disabled}
      />

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: 24, horizontal: 30 }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              width: menuWidth,
              textOverflow: 'ellipsis',
              overflowX: 'hidden',
              overflowY: 'auto',
              whiteSpace: 'nowrap',
            },
          },
        }}
      >
        {items.map((item, index) => {
          const tooltip = item?.tooltip

          // Wrap MenuItem in a <span> to ensure that the tooltip appears even if
          // it's disabled (which prevents events from firing)
          // See: https://github.com/mui/material-ui/issues/8416
          return (
            <TooltipIfEnabled enabled={!!tooltip} label={tooltip} key={`${item.label}${index}`}>
              <span>
                <MenuItem
                  onClick={() => {
                    item.onClick(row)
                    handleClose()
                  }}
                  disabled={item.disabled}
                >
                  {item.label}
                </MenuItem>
              </span>
            </TooltipIfEnabled>
          )
        })}
      </Menu>
    </>
  )
}
